import React, { useState, useRef, useEffect, useCallback, Suspense, lazy } from 'react';
import pipes from './assets/Pipes.jpg';
import emptyScore from './assets/EmptyScore.png';
import lowGNote from './assets/LowGNote.png';
import './App.css';
import SideNav from './components/SideNav';
import Home from './components/Home';
import AnimatedNotes from './components/AnimatedNotes';
import FlashCard from './components/FlashCard';
import Metronome from './components/Metronome';
import Resources from './components/Resources';
import ContactForm from './components/ContactForm';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import AppSEO from './components/AppSEO';
import { initializeStorage } from './utils/storage';
import { playTone as audioPlayTone } from './utils/audioProcessor';
import { useGoogleAnalytics } from './utils/useGoogleAnalytics';
import { ensureUserUUID } from './utils/uuidUtils';
import { clearDialogInstances } from './utils/CommonDialogs';
import { setupAnalytics, trackPageView } from './utils/analytics';
// Import lighter components directly
import BasicNotes from './components/BasicNotes';
import FeatureRequest from './components/FeatureRequest';
import AboutUs from './components/AboutUs';
import Credits from './components/Credits';
import Gracenotes from './components/Gracenotes';
import LoadingComponent from './components/common/LoadingComponent';
import Settings from './components/Settings';
import MicrophoneHelp from './components/MicrophoneHelp';
import LeaderBoard from './components/LeaderBoard';
import Auth from './components/Auth';
import Blocked from './components/Blocked';
import ProtectedRoute from './components/ProtectedRoute';
import AuthCallback from './components/AuthCallback';

const isDev = process.env.NODE_ENV === 'development';

// Lazy load heavier components
const SheetMusic = lazy(() => import('./components/SheetMusic'));
const NoteBattle = lazy(() => import('./components/NoteBattle'));
const ChanterSync = lazy(() => import('./components/ChanterSync'));
const ChanterTest = lazy(() => import('./components/ChanterTest'));
const Blaster = lazy(() => import('./components/Blaster'));
const Simon = lazy(() => import('./components/Simon'));
const Echo = lazy(() => import('./components/Echo'));
const Games = lazy(() => import('./components/Games'));
const ChanterRender = lazy(() => import('./components/ChanterRender'));
const BlasterHighScores = lazy(() => import('./components/BlasterHighScores'));
const PlayAlong = lazy(() => import('./components/PlayAlong'));

// Dev-only experiments (routed only when isDev; components kept in repo)
const AITest = isDev ? lazy(() => import('./components/AITest')) : null;
const RenderTest = isDev
  ? lazy(() => import('./components/RenderTest').then(module => ({ default: module.RenderTest })))
  : null;
const FireEffectDemo = isDev ? lazy(() => import('./components/FireEffectDemo')) : null;
const Template1 = isDev ? lazy(() => import('./components/Template1')) : null;
const Template2 = isDev ? lazy(() => import('./components/Template2')) : null;
const DatabaseTest = isDev ? lazy(() => import('./components/DatabaseTest')) : null;
const AnimateSong = isDev ? lazy(() => import('./components/AnimateSong')) : null;
const DirectFunctionTest = isDev ? lazy(() => import('./components/DirectFunctionTest')) : null;
const PitchTestbedPage = isDev ? lazy(() => import('./testbed/pitch/PitchTestbedPage')) : null;

// Lazy load Instruction and Profile components
const Instruction = lazy(() => import('./components/Instruction'));
const Profile = lazy(() => import('./components/Profile'));

// Lazy load BandFinder component
const BandFinder = lazy(() => import('./components/BandFinder'));

// Lazy load BandRegistration component
const BandRegistration = lazy(() => import('./components/BandRegistration'));

// Lazy load BandProfile component
const BandProfile = lazy(() => import('./components/BandProfile'));

// Lazy load BandDetail component  
const BandDetail = lazy(() => import('./components/BandDetail'));

// Lazy load AISheetNovel component
const AISheetNovel = lazy(() => import('./components/AISheetNovel'));
const PlaylistDashboard = lazy(() => import('./components/PlaylistDashboard'));
const PlaylistAccess = lazy(() => import('./components/PlaylistAccess'));

// Lazy load OMR component
const OMR = lazy(() => import('./components/OMR'));

// Lazy load SheetShare component
const SheetShare = lazy(() => import('./components/SheetShare'));

// Lazy load ImportSong component
const ImportSong = lazy(() => import('./components/ImportSong'));

// Lazy load Tuner component
const Tuner = lazy(() => import('./components/Tuner'));

// Lazy load AdminPanel component
const AdminPanel = lazy(() => import('./components/AdminPanel'));

// Lazy load BagpipeSynthesisTest component (dev-only route)
const BagpipeSynthesisTest = isDev ? lazy(() => import('./components/BagpipeSynthesisTest')) : null;

// Lazy load UserProfile component
const UserProfile = lazy(() => import('./components/UserProfile'));

// Import TutorInvitation component
import TutorInvitation from './components/TutorInvitation';

// Import BandInvitation component
import BandInvitation from './components/BandInvitation';

// Create a wrapper component that uses the Google Analytics hook and handles route changes
const AnalyticsWrapper = ({ children, setActiveSection }) => {
  useGoogleAnalytics();
  const location = useLocation();
  
  // Update active section based on route path
  useEffect(() => {
    // Extract section name from path and update active section
    const path = location.pathname.startsWith('/') ? location.pathname.substring(1) : location.pathname;
    if (path === '') {
      setActiveSection('home');
    } else {
      setActiveSection(path);
    }
  }, [location.pathname, setActiveSection]);
  
  // Check for pending redirect
  useEffect(() => {
    // Check for localStorage flag (primary method)
    const forceNavigateFlag = localStorage.getItem('_force_navigate_to_chantersync');
    if (forceNavigateFlag === 'true' && location.pathname !== '/chantersync') {
      console.log('[App Debug] Found localStorage redirect flag, directing to ChanterSync');
      // Clear the flag
      localStorage.removeItem('_force_navigate_to_chantersync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Set active section before navigation
      setActiveSection('chantersync');
      
      // Navigate directly
      window.location.replace(url);
      return;
    }
    
    // Check for sessionStorage flag (legacy/backup method)
    const shouldRedirect = sessionStorage.getItem('_redirect_to_chanter_sync');
    if (shouldRedirect === 'true' && location.pathname !== '/chantersync') {
      console.log('[App Debug] Found sessionStorage redirect flag, directing to ChanterSync');
      sessionStorage.removeItem('_redirect_to_chanter_sync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Navigate directly
      window.location.replace(url);
    }
  }, [location.pathname, setActiveSection]);
  
  // Clear dialog instances when route changes
  useEffect(() => {
    console.log('[App Debug] Route changed to:', location.pathname);
    clearDialogInstances();
  }, [location.pathname]);
  
  return children;
};

// Helper function for safely checking migration notice status
const checkForMigrationNotice = () => {
  try {
    const { shouldShowMigrationNotice } = require('./utils/globalSettings');
    return shouldShowMigrationNotice();
  } catch (error) {
    console.warn('Could not check migration notice status:', error);
    return false;
  }
};

// Helper function for dismissing the notice
const dismissMigrationNoticeHelper = () => {
  try {
    const { dismissMigrationNotice } = require('./utils/globalSettings');
    return dismissMigrationNotice();
  } catch (error) {
    console.warn('Could not dismiss migration notice:', error);
    return false;
  }
};

function App() {
  const [activeSection, setActiveSection] = useState('home');
  const [isInitialized, setIsInitialized] = useState(false);
  const [imageHeight, setImageHeight] = useState(0);
  const [migrationStatus, setMigrationStatus] = useState({
    needsMigration: false,
    showNotice: false,
    migrated: false
  });
  const [userUUID, setUserUUID] = useState(null);
  const fullscoreRef = useRef(null);
  const metronomeRef = useRef(null);

  // Effect to dynamically set root element widths to match viewport
  useEffect(() => {
    const updateRootWidth = () => {
      const viewportWidth = window.innerWidth;
      document.documentElement.style.width = `${viewportWidth}px`;
      document.body.style.width = `${viewportWidth}px`;
      const rootElement = document.getElementById('root');
      if (rootElement) {
        rootElement.style.width = `${viewportWidth}px`;
      }
      const appElement = document.querySelector('.App');
      if (appElement) {
        appElement.style.width = `${viewportWidth}px`;
      }
    };

    updateRootWidth();
    window.addEventListener('resize', updateRootWidth);
    return () => window.removeEventListener('resize', updateRootWidth);
  }, []);

  // First effect: Check for force navigation flag before anything else
  useEffect(() => {
    const forceNavigateFlag = localStorage.getItem('_force_navigate_to_chantersync');
    if (forceNavigateFlag === 'true') {
      console.log('[App Debug] Found force navigate flag, directing to ChanterSync');
      // Clear the flag to prevent loops
      localStorage.removeItem('_force_navigate_to_chantersync');
      
      // Use the full, absolute URL
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/chantersync`;
      
      // Set activeSection immediately to prevent incorrect state
      setActiveSection('chanter-sync');
      
      // Navigate directly
      window.location.replace(url);
    }
  }, []);

  // Get active section from localStorage on load
  useEffect(() => {
    const storedActiveSection = localStorage.getItem('active_section');
    if (storedActiveSection) {
      console.log('[App Debug] Setting active section from localStorage:', storedActiveSection);
      setActiveSection(storedActiveSection);
      
      // Clear it after use to avoid issues on other page loads
      localStorage.removeItem('active_section');
    }
  }, []);

  useEffect(() => {
    // Initialize storage and check for migrations
    const initResult = initializeStorage(true); // true = run automatic migrations
    
    console.log('Storage initialization result:', initResult);
    
    // Ensure we have a UUID for the user
    const uuid = ensureUserUUID();
    setUserUUID(uuid);
    console.log('User UUID initialized:', uuid);
    
    // Set initial status
    setMigrationStatus({
      needsMigration: initResult.needsMigration,
      showNotice: false, // Initialize to false, we'll check again after a delay
      migrated: initResult.migrated
    });
    
    setIsInitialized(true);
    
    // Check for migration notice after a short delay to ensure all modules are loaded
    const noticeCheckTimer = setTimeout(() => {
      const showNotice = checkForMigrationNotice();
      setMigrationStatus(prev => ({
        ...prev,
        showNotice
      }));
    }, 500);
    
    return () => clearTimeout(noticeCheckTimer);
  }, []);

  // Handle dismissing the migration notice
  const handleDismissMigrationNotice = () => {
    dismissMigrationNoticeHelper();
    setMigrationStatus(prev => ({
      ...prev,
      showNotice: false
    }));
  };

  useEffect(() => {
    if (fullscoreRef.current) {
      const updateHeight = () => {
        setImageHeight(fullscoreRef.current.clientHeight);
      };
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [activeSection]);

  const playTone = useCallback((frequency, audioContext, options = {}) => {
    return audioPlayTone(frequency, audioContext, options);
  }, []);

  // Array of 9 identical images for the row
  const rowImages = Array(9).fill(lowGNote);

  if (!isInitialized) {
    return <div>Loading...</div>;
  }

      return (
      <Router>
        <HelmetProvider>
          <AnalyticsWrapper setActiveSection={setActiveSection}>
            <AppSEO />
            <div className="App app-shell">
              <SideNav />
              <div className="app-shell__content">
            {/* Show migration notice if needed */}
            {migrationStatus.showNotice && (
              <div className="migration-notice">
                <div className="migration-notice-content">
                  <p>Your settings have been updated. Please verify your microphone and chanter selections.</p>
                  <button 
                    className="migration-notice-button"
                    onClick={handleDismissMigrationNotice}
                  >
                    Got it
                  </button>
                  <button 
                    className="migration-notice-button migration-notice-settings-button"
                    onClick={() => {
                      setActiveSection('settings');
                      window.location.href = '/settings';
                      handleDismissMigrationNotice();
                    }}
                  >
                    Go to Settings
                  </button>
                </div>
              </div>
            )}
            
            <main>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/flashcards" element={<FlashCard />} />
                <Route path="/metronome" element={<Metronome ref={metronomeRef} />} />
                <Route path="/basic-notes" element={<BasicNotes playTone={playTone} />} />
                <Route path="/play-along" element={
                  <Suspense fallback={<LoadingComponent message="Loading Play Along..." />}>
                    <PlayAlong />
                  </Suspense>
                } />
                
                {/* Lazy-loaded routes */}
                <Route path="/chanter-sync" element={
                  <Suspense fallback={<LoadingComponent message="Loading Manage Chanters..." />}>
                    <ChanterSync />
                  </Suspense>
                } />
                <Route path="/chantersync" element={
                  <Suspense fallback={<LoadingComponent message="Loading Manage Chanters..." />}>
                    <ChanterSync />
                  </Suspense>
                } />
                <Route path="/sheet-music" element={<Suspense fallback={<LoadingComponent />}><SheetMusic /></Suspense>} />
                <Route path="/chanter-test" element={
                  <Suspense fallback={<LoadingComponent message="Loading Chanter Test..." />}>
                    <ChanterTest />
                  </Suspense>
                } />
                <Route path="/games" element={
                  <Suspense fallback={<LoadingComponent message="Loading Games..." />}>
                    <Games />
                  </Suspense>
                } />
                {/* Saved bookmarks / old links → Note Battle */}
                <Route path="/battle" element={<Navigate to="/note-battle" replace />} />
                <Route path="/note-battle" element={
                  <Suspense fallback={<LoadingComponent message="Loading Note Battle..." />}>
                    <NoteBattle />
                  </Suspense>
                } />
                <Route path="/blaster" element={
                  <Suspense fallback={<LoadingComponent message="Loading Blaster Game..." />}>
                    <Blaster />
                  </Suspense>
                } />
                <Route path="/simon" element={
                  <Suspense fallback={<LoadingComponent message="Loading Melodic Simon..." />}>
                    <Simon />
                  </Suspense>
                } />
                <Route path="/echo" element={
                  <Suspense fallback={<LoadingComponent message="Loading Phrase Echo..." />}>
                    <Echo />
                  </Suspense>
                } />
                <Route path="/animated-notes" element={
                  <Suspense fallback={<LoadingComponent message="Loading Animated Notes..." />}>
                    <AnimatedNotes />
                  </Suspense>
                } />
                <Route path="/chanter-render" element={
                  <Suspense fallback={<LoadingComponent message="Loading Chanter View..." />}>
                    <ChanterRender />
                  </Suspense>
                } />
                <Route path="/gracenotes" element={
                  <Suspense fallback={<LoadingComponent message="Loading Gracenotes..." />}>
                    <Gracenotes />
                  </Suspense>
                } />
                
                {/* Regular routes */}
                <Route path="/resources" element={<Resources />} />
                <Route path="/contact" element={<ContactForm />} />
                <Route path="/about-us" element={<AboutUs />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/feature-request" element={<FeatureRequest />} />
                <Route path="/microphone-help" element={<MicrophoneHelp />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/leader-board" element={<LeaderBoard />} />

                <Route path="/tuner" element={
                  <Suspense fallback={<LoadingComponent message="Loading Tuner..." />}>
                    <Tuner />
                  </Suspense>
                } />
                <Route path="/blaster-high-scores" element={
                  <Suspense fallback={<LoadingComponent message="Loading High Scores..." />}>
                    <BlasterHighScores />
                  </Suspense>
                } />

                {/* Dev-only experiments — not registered in production builds */}
                {isDev && (
                  <>
                    <Route path="/render-test" element={
                      <Suspense fallback={<LoadingComponent message="Loading Render Test..." />}>
                        <RenderTest />
                      </Suspense>
                    } />
                    <Route path="/fire-demo" element={
                      <Suspense fallback={<LoadingComponent message="Loading Fire Demo..." />}>
                        <FireEffectDemo />
                      </Suspense>
                    } />
                    <Route path="/template1" element={
                      <Suspense fallback={<LoadingComponent message="Loading Template 1..." />}>
                        <Template1 />
                      </Suspense>
                    } />
                    <Route path="/template2" element={
                      <Suspense fallback={<LoadingComponent message="Loading Template 2..." />}>
                        <Template2 />
                      </Suspense>
                    } />
                    <Route path="/database-test" element={
                      <Suspense fallback={<LoadingComponent message="Loading Database Test..." />}>
                        <DatabaseTest />
                      </Suspense>
                    } />
                    <Route path="/animate-song" element={
                      <Suspense fallback={<LoadingComponent message="Loading Song Animation..." />}>
                        <AnimateSong />
                      </Suspense>
                    } />
                    <Route path="/aitest" element={
                      <Suspense fallback={<LoadingComponent message="Loading AI Analysis..." />}>
                        <AITest />
                      </Suspense>
                    } />
                    <Route path="/ai-test" element={
                      <Suspense fallback={<LoadingComponent message="Loading AI Analysis..." />}>
                        <AITest />
                      </Suspense>
                    } />
                    <Route path="/directfunctiontest" element={
                      <Suspense fallback={<LoadingComponent message="Loading Function Test..." />}>
                        <DirectFunctionTest />
                      </Suspense>
                    } />
                    <Route path="/synthesis-test" element={
                      <Suspense fallback={<LoadingComponent message="Loading Synthesis Test..." />}>
                        <BagpipeSynthesisTest />
                      </Suspense>
                    } />
                    <Route path="/dev/pitch-testbed" element={
                      <Suspense fallback={<LoadingComponent message="Loading Pitch Testbed..." />}>
                        <PitchTestbedPage />
                      </Suspense>
                    } />
                  </>
                )}

                <Route path="/auth" element={<Auth />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/blocked" element={<Blocked />} />
                <Route element={<ProtectedRoute />}>
                  <Route path="/profile" element={
                    <Suspense fallback={<LoadingComponent message="Loading Profile..." />}>
                      <Profile />
                    </Suspense>
                  } />
                  <Route path="/user-profile" element={
                    <Suspense fallback={<LoadingComponent message="Loading User Profile..." />}>
                      <UserProfile />
                    </Suspense>
                  } />
                </Route>
                <Route path="/instruction" element={
                  <Suspense fallback={<LoadingComponent message="Loading Instruction..." />}>
                    <Instruction />
                  </Suspense>
                } />
                <Route path="/bands" element={
                  <Suspense fallback={<LoadingComponent message="Loading Band Finder..." />}>
                    <BandFinder />
                  </Suspense>
                } />
                <Route path="/band-registration" element={
                  <Suspense fallback={<LoadingComponent message="Loading Band Registration..." />}>
                    <BandRegistration />
                  </Suspense>
                } />
                <Route path="/band-profile" element={
                  <Suspense fallback={<LoadingComponent message="Loading Band Profile..." />}>
                    <BandProfile />
                  </Suspense>
                } />
                <Route path="/band/:bandId" element={
                  <Suspense fallback={<LoadingComponent message="Loading Band Details..." />}>
                    <BandDetail />
                  </Suspense>
                } />
                <Route path="/tutor-invitation" element={<TutorInvitation />} />
                <Route path="/band-invitation" element={<BandInvitation />} />
                <Route path="/aisheetnovel" element={
                  <Suspense fallback={<LoadingComponent message="Loading AI Sheet Novel..." />}>
                    <AISheetNovel />
                  </Suspense>
                } />
                <Route path="/omr" element={
                  <Suspense fallback={<LoadingComponent message="Loading OMR..." />}>
                    <OMR />
                  </Suspense>
                } />
                <Route path="/sheet-share" element={
                  <Suspense fallback={<LoadingComponent message="Loading Sheet Share..." />}>
                    <SheetShare />
                  </Suspense>
                } />
                <Route element={<ProtectedRoute />}>
                  <Route path="/playlists" element={
                    <Suspense fallback={<LoadingComponent message="Loading Playlists..." />}>
                      <PlaylistDashboard />
                    </Suspense>
                  } />
                </Route>
                <Route path="/playlists/access" element={
                  <Suspense fallback={<LoadingComponent message="Loading Playlist Access..." />}>
                    <PlaylistAccess />
                  </Suspense>
                } />
                <Route path="/import-song" element={
                  <Suspense fallback={<LoadingComponent message="Loading Import Song..." />}>
                    <ImportSong />
                  </Suspense>
                } />
                <Route path="/admin-songs" element={
                  <Suspense fallback={<LoadingComponent message="Loading Admin Panel..." />}>
                    <AdminPanel />
                  </Suspense>
                } />
                <Route path="*" element={<Navigate to="/" />} />
              </Routes>
            </main>
              </div>
          </div>
        </AnalyticsWrapper>
      </HelmetProvider>
    </Router>
  );
}

export default App;
